import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Download, Mail, AlertTriangle, Phone, Camera, FileText, Stethoscope, Scale, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import PageFrame from '../components/PageFrame';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  immediate_safety: AlertTriangle,
  documentation: Camera,
  information_exchange: FileText,
  medical: Stethoscope,
  legal_insurance: Scale,
};

const AccidentChecklist = () => {
  const [checklist, setChecklist] = useState(null);
  const [completedItems, setCompletedItems] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set(['immediate_safety']));
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchChecklist();
    // Load saved progress from localStorage
    const saved = localStorage.getItem('checklist_progress');
    if (saved) {
      setCompletedItems(new Set(JSON.parse(saved)));
    }
  }, []);

  const fetchChecklist = async () => {
    try {
      const response = await axios.get(`${API}/checklist/items`);
      setChecklist(response.data.checklist);
    } catch (error) {
      toast.error("Failed to load checklist");
    }
  };

  const toggleItem = (itemId) => {
    const newCompleted = new Set(completedItems);
    if (newCompleted.has(itemId)) {
      newCompleted.delete(itemId);
    } else {
      newCompleted.add(itemId);
    }
    setCompletedItems(newCompleted);
    localStorage.setItem('checklist_progress', JSON.stringify([...newCompleted]));
  };

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/checklist/download`, { email, source: 'checklist_page' });
      toast.success("Check your email! Opening printable checklist...");
      window.open(`${API}/checklist/pdf`, '_blank');
      setShowDownloadModal(false);
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process download");
    } finally {
      setLoading(false);
    }
  };

  const getTotalItems = () => {
    if (!checklist) return 0;
    return Object.values(checklist).reduce((total, cat) => total + cat.items.length, 0);
  };

  const getCompletedCount = () => completedItems.size;

  const getProgress = () => {
    const total = getTotalItems();
    return total > 0 ? Math.round((getCompletedCount() / total) * 100) : 0;
  };

  const getCategoryProgress = (category) => {
    if (!checklist || !checklist[category]) return 0;
    const items = checklist[category].items;
    const completed = items.filter(item => completedItems.has(item.id)).length;
    return items.length > 0 ? Math.round((completed / items.length) * 100) : 0;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'border-red-500 bg-red-500/10';
      case 'high': return 'border-orange-500 bg-orange-500/10';
      default: return 'border-gray-600 bg-gray-700/30';
    }
  };

  if (!checklist) {
    return (
      <PageFrame title="Loading...">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </PageFrame>
    );
  }

  return (
    <>
      <SEO 
        title="Truck Accident Checklist - What To Do After an Accident"
        description="Interactive checklist to guide you through what to do after a truck accident. Download the printable PDF to keep in your vehicle."
      />
      
      <PageFrame 
        title="Truck Accident Checklist"
        subtitle="Know exactly what to do after a truck accident. Check off items as you complete them."
      >
        {/* Emergency Banner */}
        <div className="bg-red-500/20 border-2 border-red-500 rounded-xl p-4 mb-8 flex items-center gap-4">
          <Phone className="h-8 w-8 text-red-500 flex-shrink-0" />
          <div>
            <h3 className="font-bold text-red-400 text-lg">Emergency: Call 911</h3>
            <p className="text-gray-600 dark:text-gray-400">If anyone is injured or there is significant damage, call emergency services immediately.</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Your Progress</span>
            <span className="text-sm font-bold text-orange-500">{getCompletedCount()} of {getTotalItems()} completed ({getProgress()}%)</span>
          </div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-orange-400 transition-all duration-500"
              style={{ width: `${getProgress()}%` }}
            />
          </div>
        </div>

        {/* Download Button */}
        <div className="flex justify-center mb-8">
          <Button 
            onClick={() => setShowDownloadModal(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 text-lg"
          >
            <Download className="mr-2 h-5 w-5" />
            Download Printable PDF
          </Button>
        </div>

        {/* Checklist Categories */}
        <div className="space-y-4">
          {Object.entries(checklist).map(([key, category]) => {
            const Icon = CATEGORY_ICONS[key] || FileText;
            const isExpanded = expandedCategories.has(key);
            const progress = getCategoryProgress(key);
            
            return (
              <Card key={key} className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => toggleCategory(key)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-500/20">
                      <Icon className="h-5 w-5 text-orange-500" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{category.title}</h3>
                      <p className="text-sm text-gray-500">{progress}% complete</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-orange-500 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>
                
                {isExpanded && (
                  <CardContent className="pt-0 pb-4 px-4">
                    <div className="space-y-2 mt-2">
                      {category.items.map((item) => {
                        const isCompleted = completedItems.has(item.id);
                        return (
                          <button
                            key={item.id}
                            onClick={() => toggleItem(item.id)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border-l-4 transition-all ${
                              isCompleted 
                                ? 'bg-green-500/10 border-green-500' 
                                : getPriorityColor(item.priority)
                            }`}
                          >
                            {isCompleted ? (
                              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                            ) : (
                              <Circle className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            )}
                            <span className={`text-left ${isCompleted ? 'line-through text-gray-500' : 'text-gray-800 dark:text-gray-200'}`}>
                              {item.text}
                            </span>
                            {item.priority === 'critical' && !isCompleted && (
                              <span className="ml-auto text-xs font-medium text-red-500 bg-red-500/20 px-2 py-1 rounded">CRITICAL</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Reset Button */}
        <div className="flex justify-center mt-8">
          <Button 
            variant="outline"
            onClick={() => {
              setCompletedItems(new Set());
              localStorage.removeItem('checklist_progress');
              toast.success("Progress reset");
            }}
            className="text-gray-600 dark:text-gray-400"
          >
            Reset Checklist
          </Button>
        </div>

        {/* Download Modal */}
        <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
          <DialogContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">Download Printable Checklist</DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-400">
                Enter your email to receive the PDF checklist and safety updates.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleDownload} className="space-y-4 mt-4">
              <div>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-orange-500 hover:bg-orange-600"
                disabled={loading}
              >
                {loading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Get PDF Checklist
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                We'll also send you important safety updates. Unsubscribe anytime.
              </p>
            </form>
          </DialogContent>
        </Dialog>
      </PageFrame>
    </>
  );
};

export default AccidentChecklist;
